<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { X } from 'lucide-vue-next'

const props = defineProps<{ title: string }>()
const emit = defineEmits<{ close: [] }>()
const dialogRef = ref<HTMLElement | null>(null)
const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') { emit('close'); return }
  if (event.key !== 'Tab' || !dialogRef.value) return
  const focusable = Array.from(dialogRef.value.querySelectorAll<HTMLElement>(focusableSelector))
  if (focusable.length === 0) return
  const first = focusable[0]!
  const last = focusable[focusable.length - 1]!
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  dialogRef.value?.querySelector<HTMLElement>(focusableSelector)?.focus()
})
onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <Teleport to="body">
    <div class="ck-modal-backdrop" @click.self="emit('close')">
      <div ref="dialogRef" class="ck-modal" role="dialog" aria-modal="true" :aria-label="props.title">
        <header>
          <h2>{{ props.title }}</h2>
          <button type="button" class="close" aria-label="Fechar" @click="emit('close')"><X :size="20"/></button>
        </header>
        <div class="body"><slot /></div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ck-modal-backdrop { position: fixed; inset: 0; z-index: 200; display: flex; align-items: center; justify-content: center; padding: 24px; background: rgba(22, 19, 15, .6); }
.ck-modal { width: min(640px, 100%); max-height: 90vh; display: flex; flex-direction: column; border-radius: var(--radius-xl); background: var(--color-surface); color: var(--color-text); box-shadow: var(--shadow-xl); }
.ck-modal header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; border-bottom: 1px solid var(--color-border); flex-shrink: 0; }
.ck-modal h2 { margin: 0; font-family: var(--font-display); font-size: 20px; font-weight: 700; }
.close { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border: 0; border-radius: var(--radius-full); background: transparent; color: var(--color-text-muted); cursor: pointer; }
.close:hover, .close:focus-visible { background: var(--color-surface-muted); }
.ck-modal .body { padding: 24px; overflow-y: auto; }

@media (prefers-reduced-motion: no-preference) {
  .ck-modal-backdrop { animation: fade-in 160ms ease-out; }
}
@keyframes fade-in { from { opacity: 0 } to { opacity: 1 } }
</style>
